import React, { useState, useEffect, useCallback } from 'react';
import { fetchSubmissions, deleteSubmission } from '../../api/client';
import formatDate from '../../utils/formatDate';
import './SubmissionsList.css';

export default function SubmissionsList() {
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSubmissions({ limit, skip });
      setSubmissions(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmId === id) {
      try {
        await deleteSubmission(id);
        setConfirmId(null);
        loadSubmissions();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmId(id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmId(null);
  };

  if (loading && submissions.length === 0) {
    return <div className="loading">Loading submissions...</div>;
  }

  if (error) {
    return <div className="error-message">Failed to load submissions: {error}</div>;
  }

  if (submissions.length === 0) {
    return <div className="card"><p>No submissions yet.</p></div>;
  }

  const hasMore = skip + limit < total;
  const hasPrev = skip > 0;

  return (
    <div className="card">
      <h3>Lead Submissions</h3>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Phone Number</th>
            <th>Assistant ID</th>
            <th>Fields</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((sub) => (
            <React.Fragment key={sub._id}>
              <tr
                onClick={() => toggleExpand(sub._id)}
                style={{ cursor: 'pointer' }}
              >
                <td>{formatDate(sub.created_at)}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {sub.phone_number || 'N/A'}
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {sub.assistant_id
                    ? sub.assistant_id.slice(0, 12) + '…'
                    : 'N/A'}
                </td>
                <td>
                  <span className="badge badge-neutral">
                    {Object.keys(sub.details || {}).length} fields
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  {confirmId === sub._id ? (
                    <>
                      <button className="btn-danger-sm" onClick={(e) => handleDelete(e, sub._id)}>Confirm</button>
                      {' '}
                      <button className="btn-secondary-sm" onClick={handleCancelDelete}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn-icon" onClick={(e) => handleDelete(e, sub._id)} title="Delete">🗑</button>
                  )}
                </td>
              </tr>
              {expandedId === sub._id && (
                <tr className="submission-detail-row">
                  <td colSpan={5}>
                    <div className="submission-details">
                      <div className="submission-meta">
                        <span className="detail-label">Call ID:</span>{' '}
                        <span className="detail-value">{sub.call_id}</span>
                      </div>
                      {Object.entries(sub.details || {}).map(([key, value]) => (
                        <div key={key} className="detail-pair">
                          <span className="detail-label">{key}:</span>{' '}
                          <span className="detail-value">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {(hasPrev || hasMore) && (
        <div className="submission-pagination">
          <button
            className="btn-secondary"
            disabled={!hasPrev}
            onClick={() => setSkip((s) => Math.max(0, s - limit))}
          >
            Previous
          </button>
          <span className="pagination-info">
            {skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasMore}
            onClick={() => setSkip((s) => s + limit)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
